import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { FORWARDING_CHECK_STATE, type ForwardingCheck } from "./types";

/**
 * The code the customer's mail provider sent to Volca.
 *
 * Gmail addresses it to the forwarding destination, which is us, so this screen is the
 * only place the customer can read it, and without it they cannot finish the rule. It
 * shows wherever the forwarding instructions show, including after submission: a
 * customer who sends their code request late is exactly the one who needs it there.
 */
export const ForwardingCodeNotice = ({ check }: { check: ForwardingCheck }) => {
  if (
    !check.verification_code ||
    check.state === FORWARDING_CHECK_STATE.confirmed
  ) {
    return null;
  }

  // Anyone who works out the Volca address can send us something that looks like a
  // code, and inbound mail carries no sender proof we can rely on. So they are kept
  // rather than replaced, and when more than one arrives the customer is told, because
  // they are the only one who knows which code their own provider sent them.
  const older = (check.verification_codes ?? [])
    .map((entry) => entry.code)
    .filter((code) => code !== check.verification_code)
    .reverse();

  return (
    <Stack className="w-full" align="start" gap={1}>
      <Text weight="semibold" variant="caption">
        Your email provider asked us for a code
      </Text>
      <Text className="select-text">{check.verification_code}</Text>
      <Text tone="muted">
        Enter it in your email settings to turn the forwarding on.
      </Text>
      {older.length > 0 && (
        <Text tone="muted">
          More than one code reached your Volca address. Try the one above
          first. The others were{" "}
          <span className="font-semibold select-text">{older.join(", ")}</span>.
        </Text>
      )}
    </Stack>
  );
};
